// breakdown-filter-sidebar.component.ts

import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PointLeaderboardService,
  User,
  Season,
  TimelineResponse,
  CategoryLevel,
  Category,
  RatingFilterConfig,
  CategoryItem,
  CLUBS_PAGE_SIZE,
  GetUsersParams,
  PlayerCategory,
  ParentRoleFilter,
  ClassificationFilter,
  isNonClassified,
  DEFAULT_PLAYER_CATEGORY,
} from '../../../utilities/services/point-leaderboard/point-leaderboard.service';

/** A Player-role dropdown profile field (e.g. Para) offered as a filter. */
export interface ClassificationFieldDef {
  name: string;
  values: string[];
}
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TruncatePipe } from '../../../pipes/truncate.pipe';

interface CustomEvent {
  event_id: number;
  event_name: string;
  checked?:boolean;
}

interface PlayerCategoryOption {
  value: PlayerCategory;
  labelKey: string;
}

@Component({
  selector: 'stupa-breakdown-filter-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TruncatePipe],
  templateUrl: './breakdown-filter-sidebar.component.html',
  styleUrl: './breakdown-filter-sidebar.component.scss'
})
export class BreakdownFilterSidebarComponent implements OnInit, OnChanges {
  
  constructor(
    private readonly pointLeaderboardService: PointLeaderboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
  ) {}

  @Output() panelClose = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<{
    parents: number[];
    rank_range?: string[];
    period?: { value: string };
    event_ids?: number[];
    genders?: string[];
    ages?: number[];
    category_id?: number;
    level_id?: number;
    player_category?: PlayerCategory;
    parent_role_filters?: ParentRoleFilter[];
    classification_filters?: ClassificationFilter[];
  }>();

  @Input() totalItems: number = 0;
  // State from parent (parents = applied filter IDs)
  @Input() parents: number[] = [];
  @Input() rank_range: string[] = [];
  @Input() period: { value: string } | null = null;
  @Input() filterEventIds: number[] = [];
  @Input() genders: string[] = [];
  @Input() ages: number[] = [];
  @Input() category_id: number | null = null;
  @Input() player_category: PlayerCategory | null = null;
  @Input() publishedDates: string[] =[];
  @Input() selectedCategoryLevelId: number | null = null;
  @Input() selectedCategoryLevelName: string | null = null;
  @Input() filterConfig!: RatingFilterConfig;
  @Input() parent_role_list: any[] = []; // From parent (rating-leaderboard) - no duplicate API call
  @Input() clubsRoleId: number | null = null;
  @Input() classificationFields: ClassificationFieldDef[] = [];
  @Input() classification_filters: ClassificationFilter[] = [];

  // Component state
  activeTab: string = 'playerCategory';
  activeRoleId: number | null = null;
  filteredRoleEntities: any[] = [];
  clubs: User[] = [];
  selectedClubs: { [key: number]: boolean } = {};
  searchValue: string = '';

  seasons: Season[] = [];
  timeline: TimelineResponse | null = null;
  selectedTimelineId: string | null = null;
  selectedSeasonId: string = '';

  rankOptions: string[] = ['Top 20', '20-50', '50-100', '100-200', '200-300', '300-400', '400-500', '500-600', '600-700', '700-800', '800-900', '900-1000', '1000 & Up'];
  filteredRankOptions: string[] = [];
  selectedRank: string | null = null;

  events: CustomEvent[] = [];
  // selectedEvents: { [key: number]: boolean } = {};
  eventTabSeasonId: string = '';

  selectedGenders: { [key: string]: boolean } = {};
  
  ageOptions: number[] = [12, 13, 14, 15, 17, 20, 30, 40, 50, 60, 65, 70, 75, 80];
  selectedAges: { [key: number]: boolean } = {};
  
  categoryLevels: CategoryLevel[] = [];
  // allCategories: Category[] = [];
  // categoriesForFilter: Category[] = [];
  allCategories: CategoryItem[] = [];
  categoriesForFilter: CategoryItem[] = [];
  selectedCategoryId: number | null = null;

  playerCategoryOptions: PlayerCategoryOption[] = [
    { value: 'active_player', labelKey: 'active' },
    { value: 'inactive_player', labelKey: 'inactive' },
  ];
  selectedPlayerCategory: PlayerCategory = DEFAULT_PLAYER_CATEGORY;

  // Per classification field: which values are ticked, including the
  // NON_CLASSIFIED_VALUE row. Default is all ticked (= filter inactive, omitted
  // from the apply payload); none ticked is sent as selected_values: [] and
  // matches nobody (the empty state).
  selectedClassValues: { [fieldName: string]: { [value: string]: boolean } } = {};
  classificationSearch: { [fieldName: string]: string } = {};

  private readonly searchSubject = new Subject<string>();
  ngOnInit() {
    this.initFromParentRoleList();
    this.initializeFilters();
    this.fetchInitialData();
      this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchValue) => {
          const params = this.buildClubsUsersParams(1, searchValue);
          if (!params) {
            return of({ users: [], count: 0 });
          }
          return this.pointLeaderboardService.getUsers(params);
        }),
      )
      .subscribe(({ users }) => {
        this.clubs = users;
        const newSelectedClubs = { ...this.selectedClubs };
        this.clubs.forEach((club) => {
          if (!(club.user_role_id in newSelectedClubs)) {
            newSelectedClubs[club.user_role_id] = this.parents.includes(
              club.user_role_id
            );
          }
        });
        this.selectedClubs = newSelectedClubs;
        this.filterRankOptions();
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalItems']) {
      this.filterRankOptions();
    }
    if (changes['parents']) {
      if (this.parent_role_list?.length > 0) {
        this.initializeSelectedFromParents();
      } else {
        this.selectedClubs = this.parents.reduce((acc, id) => ({ ...acc, [id]: true }), {});
      }
    }
    if (changes['parent_role_list']) {
      this.initFromParentRoleList();
    }
    if (changes['player_category']) {
      this.selectedPlayerCategory = this.player_category ?? DEFAULT_PLAYER_CATEGORY;
      this.cdr.detectChanges();
    }
    if (changes['classificationFields'] ?? changes['classification_filters'] ?? changes['filterConfig']) {
      this.initClassificationSelections();
    }
    if (changes['publishedDates'] && !this.period?.value) {
      // No period applied yet => pre-select "Latest" (newest-first list).
      this.selectedTimelineId = this.publishedDates?.[0] || null;
    }
  }

  private initFromParentRoleList(): void {
    if (this.parent_role_list?.length > 0) {
      this.activeRoleId = this.parent_role_list[0].parent_role_id;
      this.updateFilteredRoleEntities();
      this.initializeSelectedFromParents();
    }
    this.cdr.detectChanges();
  }

  private initializeFilters() {
    if (this.parent_role_list?.length > 0) {
      this.initializeSelectedFromParents();
    } else {
      this.selectedClubs = this.parents.reduce((acc, id) => ({ ...acc, [id]: true }), {});
    }
    this.selectedGenders = this.genders.reduce((acc, gender) => ({ ...acc, [gender]: true }), {});
    this.selectedAges = this.ages.reduce((acc, age) => ({ ...acc, [age]: true }), {});
    this.selectedCategoryId = this.category_id;
    this.selectedPlayerCategory = this.player_category ?? DEFAULT_PLAYER_CATEGORY;
    this.selectedTimelineId = this.period?.value || this.publishedDates?.[0] || null;
    this.initClassificationSelections();

    if (this.rank_range.length > 0) {
        const matchingOption = this.rankOptions.find(option => {
            const range = this.getRankRange(option);
            return range[0] === this.rank_range[0] && range[1] === this.rank_range[1];
        });
        this.selectedRank = matchingOption || null;
    }
  }

  private fetchInitialData() {
    this.fetchClubs();
    this.fetchSeasons();
    this.filterRankOptions();
    this.fetchEvents();
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'club' && this.parent_role_list?.length > 0 && !this.activeRoleId) {
      this.activeRoleId = this.parent_role_list[0].parent_role_id;
      this.updateFilteredRoleEntities();
    }
  }

  onClubSelect(userRoleId: number) {
    this.selectedClubs[userRoleId] = !this.selectedClubs[userRoleId];
    this.cdr.detectChanges();
  }

  onRankSelect(option: string | null) {
    this.selectedRank = option;
    this.cdr.detectChanges();
  }

  onPlayerCategorySelect(category: PlayerCategory) {
    this.selectedPlayerCategory = category;
    this.cdr.detectChanges();
  }

  isRoleEntitySelected(parentUserRoleId: number): boolean {
    return !!this.selectedClubs[parentUserRoleId];
  }

  onRoleEntitySelect(parentUserRoleId: number) {
    this.selectedClubs[parentUserRoleId] = !this.selectedClubs[parentUserRoleId];
    this.cdr.detectChanges();
  }

  // Association-role tabs honour the admin filter configuration: when the
  // tenant has configured player_association_roles, only those roles are
  // offered (RNR also strips non-configured roles on the OTT path); an empty
  // config keeps the legacy show-all behaviour.
  getDisplayableRoles(): any[] {
    const configured = this.filterConfig?.player_association_roles;
    if (configured?.length > 0) {
      return (this.parent_role_list || []).filter((r: any) => configured.includes(r.parent_role_name));
    }
    return this.parent_role_list || [];
  }

  // Classification tabs = the fields enabled in the filter configuration that
  // still exist as Player dropdown fields in URS.
  getVisibleClassificationFields(): ClassificationFieldDef[] {
    const enabled = this.filterConfig?.ott_leaderboard?.enabled_classification_fields;
    if (!enabled?.length) return [];
    return (this.classificationFields || []).filter((f) => enabled.includes(f.name));
  }

  // Rating Period is admin-gated for OTT users (RNR also drops as_of_date on
  // the OTT path when the toggle is off).
  get isRatingPeriodEnabled(): boolean {
    return !!this.filterConfig?.ott_leaderboard?.enable_rating_period && this.publishedDates?.length > 0;
  }

  getActiveClassificationField(): ClassificationFieldDef | null {
    return this.getVisibleClassificationFields().find((f) => f.name === this.activeTab) ?? null;
  }

  getSelectedCountForClassification(field: ClassificationFieldDef): number {
    const selection = this.selectedClassValues[field.name] || {};
    return field.values.filter((v) => selection[v]).length;
  }

  /** The label as actually rendered: the sentinel row shows its localised
   *  "None (non-X players)" text. Search filters on this, not the wire value,
   *  so the row stays findable by what the user can actually see. */
  displayLabel(field: ClassificationFieldDef, value: string): string {
    return isNonClassified(value)
      ? this.translate.instant('none_non_classified', { name: field.name })
      : value;
  }

  // Active = some values unticked. All ticked means "no filtering on this
  // field" and the field is omitted from the apply payload.
  isClassificationFilterActive(field: ClassificationFieldDef): boolean {
    return this.getSelectedCountForClassification(field) < field.values.length;
  }

  getFilteredClassificationValues(field: ClassificationFieldDef): string[] {
    const term = (this.classificationSearch[field.name] || '').toLowerCase().trim();
    return term
      ? field.values.filter((v) => this.displayLabel(field, v).toLowerCase().includes(term))
      : field.values;
  }

  // Reflects the whole field (not just searched rows) so it stays consistent
  // with isClassificationFilterActive, which is computed over all values.
  areAllClassificationValuesSelected(field: ClassificationFieldDef): boolean {
    return field.values.length > 0 && field.values.every((v) => this.selectedClassValues[field.name]?.[v]);
  }

  // Select All / Deselect All toggles every value for the field (a true
  // all/none); the search term only filters which rows render, not this action.
  toggleSelectAllClassification(field: ClassificationFieldDef): void {
    const target = !this.areAllClassificationValuesSelected(field);
    if (!this.selectedClassValues[field.name]) {
      this.selectedClassValues[field.name] = {};
    }
    field.values.forEach((v) => {
      this.selectedClassValues[field.name][v] = target;
    });
    this.cdr.detectChanges();
  }

  onClassValueSelect(fieldName: string, value: string): void {
    if (!this.selectedClassValues[fieldName]) {
      this.selectedClassValues[fieldName] = {};
    }
    this.selectedClassValues[fieldName][value] = !this.selectedClassValues[fieldName][value];
    this.cdr.detectChanges();
  }

  // All values ticked for every visible field = the no-filtering default.
  private resetClassificationSelectionsToDefault(): void {
    this.selectedClassValues = {};
    this.getVisibleClassificationFields().forEach((field) => {
      this.selectedClassValues[field.name] = {};
      field.values.forEach((v) => (this.selectedClassValues[field.name][v] = true));
    });
  }

  // Default all ticked; an applied filter entry overrides its field with
  // exactly its values — including the none-ticked state, which matches
  // nobody.
  private initClassificationSelections(): void {
    this.resetClassificationSelectionsToDefault();
    this.classificationSearch = {};
    (this.classification_filters || []).forEach((f) => {
      if (!this.selectedClassValues[f.field_name]) return;
      Object.keys(this.selectedClassValues[f.field_name]).forEach((v) => {
        this.selectedClassValues[f.field_name][v] = (f.selected_values || []).includes(v);
      });
    });
    this.cdr.detectChanges();
  }

  // Changing the rating period resets the row filters (PRD E12/AC-23): the
  // dataset swaps, so club/classification selections from another period
  // must not silently carry over.
  onPeriodChange(): void {
    Object.keys(this.selectedClubs).forEach((id) => (this.selectedClubs[Number(id)] = false));
    this.resetClassificationSelectionsToDefault();
    this.cdr.detectChanges();
  }

  getActiveRole(): any {
    if (!this.activeRoleId || !this.parent_role_list) return null;
    return this.parent_role_list.find((r: any) => r.parent_role_id === this.activeRoleId) || null;
  }

  updateFilteredRoleEntities() {
    const role = this.getActiveRole();
    if (!role?.parent_user_roles) {
      this.filteredRoleEntities = [];
      return;
    }
    const search = (this.searchValue || '').toLowerCase().trim();
    this.filteredRoleEntities = search
      ? role.parent_user_roles.filter((item: any) =>
          (item.parent_role_linked_field_value || '').toLowerCase().includes(search)
        )
      : [...role.parent_user_roles];
    this.cdr.detectChanges();
  }

  private initializeSelectedFromParents() {
    this.selectedClubs = {};
    if (!this.parent_role_list) return;
    this.parent_role_list.forEach((role: any) => {
      (role.parent_user_roles || []).forEach((item: any) => {
        const id = item.parent_user_role_id;
        this.selectedClubs[id] = this.parents?.includes(id) ?? false;
      });
    });
    this.cdr.detectChanges();
  }

  setActiveRoleTab(parentRoleId: number) {
    this.activeTab = 'club';
    this.activeRoleId = parentRoleId;
    this.searchValue = '';
    this.updateFilteredRoleEntities();
    this.cdr.detectChanges();
  }
  onEventSelect(event: CustomEvent) { event.checked = !event.checked}
  onGenderSelect(gender: string) { this.selectedGenders[gender] = !this.selectedGenders[gender]; }
  onAgeSelect(age: number) { this.selectedAges[age] = !this.selectedAges[age]; }
  onCategorySelect(categoryId: number) { this.selectedCategoryId = categoryId; }
  
  onSeasonChange(seasonId: string, tab: 'period' | 'event') {
    if (tab === 'period') {
        this.selectedSeasonId = seasonId;
        this.fetchTimeline();
    } else {
        this.eventTabSeasonId = seasonId;
        this.fetchEvents();
    }
  }


  onCategoryLevelSelect(levelName: string | null) {
    
    this.selectedCategoryLevelName = levelName;
    this.selectedCategoryId = null; // Reset category selection
    this.categoriesForFilter = levelName
      ? this.allCategories.filter(cat => cat.category_level_name === levelName)
      : [];
  }
  
  getRankRange(option: string): string[] {
    if (option === 'Top 20') return ['1', '20'];
    if (option === '1000 & Up') return ['1000', ''];
    const [min, max] = option.split('-').map(s => s.trim());
    return [min, max];
  }
  
  applyFiltersHandler() {
    const payload: any = {
      parents: Object.keys(this.selectedClubs).filter(id => this.selectedClubs[Number(id)]).map(Number),
      event_ids: this.events.filter(e => e.checked).map(e => e.event_id),
      events :  this.events,
      genders: Object.keys(this.selectedGenders).filter(g => this.selectedGenders[g]),
      // FIX: Convert string key 'age' to a number for indexing.
      ages: Object.keys(this.selectedAges).filter(age => this.selectedAges[Number(age)]).map(Number),
    };

    if (this.selectedRank) {
      payload.rank_range = this.getRankRange(this.selectedRank);
    }
    // The newest date IS the latest dataset — selecting it means no period
    // filter (live-join display, no historical banner, no as_of_date).
    if (this.selectedTimelineId && this.selectedTimelineId !== this.publishedDates?.[0]) {
      payload.period = { value: this.selectedTimelineId };
    }
    if (this.selectedCategoryId !== null) {
      payload.category_id = this.selectedCategoryId;
      payload.level_id = this.selectedCategoryLevelId;
      payload.level_name = this.selectedCategoryLevelName;
    }
    // Always sent — the leaderboard always has a player-status view (default
    // "Active"), never an unfiltered "both" state.
    payload.player_category = this.selectedPlayerCategory;

    // Parent-org selections grouped per role — sent as snapshot-based
    // parent_role_filters so historical periods match point-in-time clubs and
    // RNR can enforce the admin config (the flat `parents` above stays for
    // sidebar reopen state / the legacy no-role fallback).
    const parentRoleFilters = this.getDisplayableRoles()
      .map((role: any) => ({
        role_name: role.parent_role_name,
        user_role_ids: (role.parent_user_roles || [])
          .filter((item: any) => this.selectedClubs[item.parent_user_role_id])
          .map((item: any) => item.parent_user_role_id),
      }))
      .filter((f: ParentRoleFilter) => f.user_role_ids.length > 0);
    if (parentRoleFilters.length > 0) {
      payload.parent_role_filters = parentRoleFilters;
    }

    // Only fields with unticked values travel; selected_values may be []
    // (= matches nobody, the empty state). All ticked = field omitted entirely.
    const classificationFilters = this.getVisibleClassificationFields()
      .filter((field) => this.isClassificationFilterActive(field))
      .map((field) => ({
        field_name: field.name,
        selected_values: field.values.filter((v) => this.selectedClassValues[field.name]?.[v]),
      }));
    if (classificationFilters.length > 0) {
      payload.classification_filters = classificationFilters;
    }

    this.applyFilters.emit(payload);
    this.closeSidebar();
  }

clearFilters() {
  this.selectedClubs = {};
  if (this.parent_role_list?.length > 0) {
    this.parent_role_list.forEach((role: any) => {
      (role.parent_user_roles || []).forEach((item: any) => {
        this.selectedClubs[item.parent_user_role_id] = false;
      });
    });
  } else {
    this.clubs.forEach((club) => {
      this.selectedClubs[club.user_role_id] = false;
    });
  }
  this.selectedRank = null;
  this.selectedTimelineId = this.publishedDates?.[0] || null;
  this.selectedGenders = {};
  this.selectedAges = {};
  this.selectedCategoryId = null;
  this.selectedPlayerCategory = DEFAULT_PLAYER_CATEGORY;
  this.selectedCategoryLevelId = null;
  this.categoriesForFilter = [];
  this.events.forEach(event => (event.checked = false));
  this.searchValue = '';
  this.resetClassificationSelectionsToDefault();
  this.classificationSearch = {};

  this.applyFilters.emit({
    parents: [],
    rank_range: undefined,
    period: undefined,
    event_ids: [],
    genders: [],
    ages: [],
    category_id: undefined,
    level_id: undefined,
    player_category: DEFAULT_PLAYER_CATEGORY,
    parent_role_filters: [],
    classification_filters: [],
  });

  this.closeSidebar();
}
  
  private getClubsRoleId(): number | null {
    if (this.parent_role_list?.length > 0 && this.activeRoleId != null) {
      return this.activeRoleId;
    }
    return this.clubsRoleId;
  }

  private buildClubsUsersParams(
    pageNum: number,
    searchValue?: string,
  ): GetUsersParams | null {
    const roleId = this.getClubsRoleId();
    if (roleId == null) {
      return null;
    }
    return {
      roleId,
      searchValue: searchValue ?? this.searchValue,
      pageNum,
      perPage: CLUBS_PAGE_SIZE,
      skipGlobalLoader: true,
    };
  }

  // Data Fetching Methods
  fetchClubs() {
    const params = this.buildClubsUsersParams(1);
    if (!params) {
      return;
    }
    this.pointLeaderboardService
      .getUsers(params)
      .subscribe(({ users }) => {
      this.clubs = users;
      if(this.filterConfig.player_association_roles.length>0){
        this.clubs = this.clubs.filter((club)=>this.filterConfig.player_association_roles.includes(club.name) )
      }
    }); 
    
  }

  onSearch() {
    if (this.activeTab === 'club' && this.parent_role_list?.length > 0) {
      this.updateFilteredRoleEntities();
    } else {
      this.searchSubject.next(this.searchValue);
    }
  }   



  fetchSeasons() { this.pointLeaderboardService.getSeasons().subscribe(res => this.seasons = res.seasons); }
  fetchTimeline() { if (this.selectedSeasonId) this.pointLeaderboardService.getRankingTimeline(Number(this.selectedSeasonId), 0).subscribe(res => this.timeline = res); }
  fetchEvents() { 
    this.pointLeaderboardService.getRatingEvents().subscribe((response) => {
        
        this.events = response?.items?.map((item:any)=> ({  
            event_id: item?.id,
            event_name: item?.name,
            checked: this.filterEventIds.includes(item?.id)
          }))
        });
  }
  fetchCategoryLevelsAndCategories() { this.pointLeaderboardService.getCategoryLevels().subscribe({
    next: (l)=>{
      this.categoryLevels = l
      this.pointLeaderboardService
        .getLinkedCategoriesForTenant(
          environment.tenant_Name,
          undefined,
          1,
          100
        )
        .subscribe({
          next: (response) => {
            
              this.allCategories = response.items
              this.categoriesForFilter = this.selectedCategoryLevelName
                ? this.allCategories.filter(cat => cat.category_level_name === this.selectedCategoryLevelName)
                : [];
          }
        });
    }
  })}
  private filterRankOptions() { this.filteredRankOptions = this.rankOptions.filter(option => { const range = this.getRankRange(option); return this.totalItems >= Number(range[0]); }); }

  // Selected filter count getters (for badges)
  getSelectedClubCount(): number {
    return Object.keys(this.selectedClubs).filter(id => this.selectedClubs[Number(id)]).length;
  }

  getSelectedCountForRole(role: any): number {
    if (!role?.parent_user_roles) return 0;
    return role.parent_user_roles.filter(
      (item: any) => this.selectedClubs[item.parent_user_role_id]
    ).length;
  }

  getSelectedPeriodCount(): number {
    // "Latest" (the newest date) is the default dataset, not a filter.
    return this.selectedTimelineId && this.selectedTimelineId !== this.publishedDates?.[0] ? 1 : 0;
  }

  getSelectedRankCount(): number {
    return this.selectedRank ? 1 : 0;
  }

  getSelectedEventCount(): number {
    return this.events.filter(e => e.checked).length;
  }

  getSelectedAgeCount(): number {
    return Object.keys(this.selectedAges).filter(age => this.selectedAges[Number(age)]).length;
  }

  getSelectedGenderCount(): number {
    return Object.keys(this.selectedGenders).filter(g => this.selectedGenders[g]).length;
  }

  getSelectedCategoryCount(): number {
    return this.selectedCategoryId === null ? 0 : 1;
  }

  getSelectedPlayerCategoryCount(): number {
    return this.selectedPlayerCategory ? 1 : 0;
  }

  // Sum of every per-tab badge count, shown as a single "active filters" tally
  // in the header and next to Clear all.
  getTotalSelectedFilterCount(): number {
    const roleCount = this.getDisplayableRoles().reduce(
      (sum, role) => sum + this.getSelectedCountForRole(role),
      0,
    );
    const classificationCount = this.getVisibleClassificationFields().reduce(
      (sum, field) => sum + (this.isClassificationFilterActive(field) ? this.getSelectedCountForClassification(field) : 0),
      0,
    );
    return (
      this.getSelectedPlayerCategoryCount() +
      roleCount +
      this.getSelectedPeriodCount() +
      this.getSelectedRankCount() +
      this.getSelectedAgeCount() +
      classificationCount
    );
  }

  // First letters of up to the first two words — used for the small avatar
  // badge next to club/role names in the filter list.
  getInitials(name: string | null | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const initials = parts[0]?.[0] ?? '';
    const second = parts.length > 1 ? parts[1][0] : '';
    return (initials + second).toUpperCase() || '?';
  }

  trackByPlayerCategory(_index: number, option: PlayerCategoryOption): PlayerCategory {
    return option.value;
  }

  // Utility Methods
  getTimelineArray(response: TimelineResponse) { return response.by_date || response.by_week || response.by_month || []; }
  closeSidebar() { this.panelClose.emit(); }

  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}
